import React from 'react'
import ReactMarkdown from 'react-markdown'
import { HashLink } from 'react-router-hash-link';
import Site from '../site'

import { Audio, Download, Image } from '../components'
import WavTable from '../wavtable'

import '../css/site.css'


/*****************************************************************************
Constants
*****************************************************************************/


const NAME = 'msg';


/*****************************************************************************
Project website
*****************************************************************************/


export default function MSG() {
  return (
    <Site name={NAME}>
      <TableOfContents/>
      <Overview/>
      <Artifacts/>
      <Listening/>
    </Site>);
}


/*****************************************************************************
Utilities
*****************************************************************************/


function Artifacts() {
  let markdown = `## Artifacts of music source separators\n` +
  `We identify and explore two types of errors caused by music source ` +
  `separators: spectral rolloff reconstruction and onset reconstruction. ` +
  `These errors are described in Section 4.5. We provide examples of ` +
  `each error and show that MSG is capable of fixing these errors.`;
  return (
    <div className='artifacts'>
      <section id='artifacts'>
        <ReactMarkdown>{markdown}</ReactMarkdown>
        <SpectralRolloff/>
        <Onsets/>
      </section>
    </div>
  );
}


function Listening() {
  let markdown = `## Listening examples\n` +
  `For each source (bass, drums, and vocals) we provide five music ` +
  `separation listening examples from each of the five baseline ` +
  `separators with and without MSG post-processing.`;
  let bass = ['02', '17', '19', '31', '43'].map((index, i) =>
    <ListeningExample experiment={`bass-${index}`} key={i}/>);
  let drums = ['05', '11', '23', '29', '44'].map((index, i) =>
    <ListeningExample experiment={`drums-${index}`} key={i} />);
  let vocals = ['01', '18', '20', '27', '35'].map((index, i) =>
    <ListeningExample experiment={`vocals-${index}`} key={i} />);
  return (
    <div className='listening-examples'>
      <section id='listening-examples'>
        <ReactMarkdown>{markdown}</ReactMarkdown>
        <section id='bass'>
          <ReactMarkdown>{`### Bass\n`}</ReactMarkdown>
          {bass}
          <div style={{ 'paddingBottom': '2rem' }}/>
        </section>
        <section id='drums'>
          <ReactMarkdown>{`### Drums\n`}</ReactMarkdown>
          {drums}
          <div style={{ 'paddingBottom': '2rem' }} />
        </section>
        <section id='vocals'>
          <ReactMarkdown>{`### Vocals\n`}</ReactMarkdown>
          {vocals}
          <div style={{ 'paddingBottom': '2rem' }} />
        </section>
        <Download
          url={'https://drive.google.com/drive/folders/12hrHo3h0L3lZUmYQSu9w9YEoInZJdr6u?usp=sharing'}
          text={'Download subjective evaluation files and results'}
        />
      </section>
    </div>
  );
}


function ListeningExample({ experiment }) {
  let columns = ['Demucs-V2', 'Demucs-V3', 'OpenUnmix', 'Spleeter', 'Wavenet'];
  return (
    <div>
      <WavTable
        name={NAME}
        experiment={`${experiment}-header`}
        extension={'mp3'}
        columns={['Source', 'Mix']}
        displayColumns={['Ground truth source', 'Mix']}
        rows={['_']}
      />
      <WavTable
        name={NAME}
        experiment={experiment}
        extension={'mp3'}
        columns={columns}
        rows={['Raw', 'MSG']}
        displayRows={['without MSG', 'with MSG']}
      />
    </div>
  );
}


function Onsets() {
  let markdown = `### Onset reconstruction\n` +
    `We provide onset strength plots corresponding to an original bass source, ` +
    `the estimated source using four separators, and the estimated ` +
    `sources with MSG. The estimated source with MSG more closely ` +
    `reconstructs the ground truth onsets. This is true whether the ` +
    `separator underestimates or overestimates the onset strength. ` +
    `You can also see that the spectrogram separators (Spleeter ` +
    `and OpenUnmix) tend to underestimate the onset strength of the bass source.`;
  let examples = ['Demucs-V2', 'OpenUnmix', 'Spleeter', 'Wavenet'].map(
    separator => <OnsetExample separator={separator}/>);
  return (
    <div className='onset section'>
      <section id='onset'>
        <ReactMarkdown>{markdown}</ReactMarkdown>
        {examples}
      </section>
    </div>
  );
}


function OnsetExample({ separator }) {
  return (
    <>
      <Image
        alt={'Spectrograms showing the effect of MSG on spectral rolloff'}
        file={`onsets-${separator}.png`}
        name={NAME}
        width={1000}
      />
      <WavTable
        name={NAME}
        experiment={`onsets-${separator}`}
        extension={'mp3'}
        columns={['Source', 'Raw', 'MSG']}
        displayColumns={['Ground truth source', separator, 'MSG']}
        rows={['_']}
      />
      <div style={{ 'paddingBottom': '2rem' }}/>
    </>
  );
}


function Overview() {
  let markdown = `We show the results of the Make it Sound Good (MSG) post-processor, our proposed generative model that improves the quality of existing separation systems. As shown in the figure below, MSG can both remove added noise and infer missing frequencies from source estimates. MSG is able to improve the output of four popular source separation systems: Demucs v2, Wavenet, Spleeter, and OpenUnmix.\n`;
  return (
    <div className='section'>
      <section id='overview'>
        <ReactMarkdown>{markdown}</ReactMarkdown>
        <Image
          alt={'Bass spectrograms with and without MSG for multiple separators'}
          caption={'Figure 1 from our paper. Spectrograms of ground-truth (left), source estimates (top), and MSG output (bottom) for the bass source.  Corresponding audio files below.'}
          file={'hero.png'}
          name={NAME}
          width={1000}
        />
        <WavTable
          name={NAME}
          experiment={`hero-header`}
          extension={'mp3'}
          columns={['Source', 'Mix']}
          displayColumns={['Ground truth source', 'Mix']}
          rows={['_']}
        />
        <WavTable
          name={NAME}
          experiment={'hero'}
          extension={'mp3'}
          columns={['Demucs-V2', 'Wavenet', 'Spleeter', 'OpenUnmix']}
          rows={['Raw', 'MSG']}
          displayRows={['without MSG', 'with MSG']}
        />
      </section>
    </div>
  );
}


function SpectralRolloff() {
  let markdown = `### Spectral rolloff\n` +
    `We provide spectrograms corresponding to an original bass source, ` +
    `the estimated source using four separators, and the estimated ` +
    `sources with MSG. On each spectrogram, the white line indicates ` +
    `the 98% rolloff frequency at each frame. You can see that the ` +
    `estimated source with MSG more closely reconstructs the ground ` +
    `truth spectral rolloff. This is true whether the separator ` +
    `underestimates or overestimates the spectral rolloff frequency. ` +
    `You can also see that waveform estimators (Demucs and Wavenet) ` +
    `tend to overestimate the rolloff frequency, while spectrogram ` +
    `separators (Spleeter and OpenUnmix) tend to underestimate the ` +
    `rolloff frequency on bass sources.`;
  return (
    <div className='spectral-rolloff section'>
      <section id='spectral-rolloff'>
        <ReactMarkdown>{markdown}</ReactMarkdown>
        <Image
          alt={'Spectrograms showing the effect of MSG on spectral rolloff'}
          file={'rolloff.png'}
          name={NAME}
          width={1000}
        />
        <WavTable
          name={NAME}
          experiment={`rolloff-header`}
          extension={'mp3'}
          columns={['Source', 'Mix']}
          displayColumns={['Ground truth source', 'Mix']}
          rows={['_']}
        />
        <WavTable
          name={NAME}
          experiment={'rolloff'}
          extension={'mp3'}
          columns={['Demucs-V2', 'Wavenet', 'Spleeter', 'OpenUnmix']}
          rows={['Raw', 'MSG']}
          displayRows={['without MSG', 'with MSG']}
        />
      </section>
    </div>
  );
}


function TableOfContents() {
  return (
    <div className='section'>
      <ReactMarkdown>{`## Table of Contents`}</ReactMarkdown>
      <ul>
        <li>
          <HashLink to="/#overview">Overview</HashLink>
        </li>
        <li>
          <HashLink to="/#artifacts">Artifacts of music source separators</HashLink>
          <ul>
            <li>
              <HashLink to="/#spectral-rolloff">Spectral rolloff</HashLink>
            </li>
            <li>
              <HashLink to="/#onset">Onset reconstruction</HashLink>
            </li>
          </ul>
        </li>
        <li>
          <HashLink to="/#listening-examples">Listening examples</HashLink>
          <ul>
            <li>
              <HashLink to="/#bass">Bass</HashLink>
            </li>
            <li>
              <HashLink to="/#drums">Drums</HashLink>
            </li>
            <li>
              <HashLink to="/#vocals">Vocals</HashLink>
            </li>
          </ul>
        </li>
        <li>
          <HashLink to="/#citation">How to cite this work</HashLink>
        </li>
      </ul>
    </div>
  );
}
